import {
  IInitEventSecurityInfo,
  IInitEventSecurityLinkChild,
  IInitEventSecurityTag,
} from 'ngx-mt-ui-lib/api-client';
import { createFeature, createReducer, on } from '@ngrx/store';
import { ISelectItem } from 'ngx-mt-ui-lib/interfaces';
import { SecurityEventsActions } from './security-events.actions';
import { compareObjects } from 'ngx-mt-ui-lib/functions';
import { EStateStatus } from '@app/app.state';

export interface ISecurityEventsState {
  id: number | null;
  item: IInitEventSecurityInfo;
  form: Partial<IInitEventSecurityInfo>;
  tags: IInitEventSecurityTag[];
  pendingItem: IInitEventSecurityInfo | null;
  remoteInfoChanged: boolean;
  infoRevision: number;
  hasChanges: boolean;
  protocolDirty: boolean;
  protocolSaving: boolean;
  loading: boolean;
  stateListWithCount: ISelectItem<{ count: number }, string>[];
  childEventList: IInitEventSecurityLinkChild[];
}

export const securityEventsInitialState: ISecurityEventsState = {
  id: null,
  item: null,
  form: null,
  tags: [],
  pendingItem: null,
  remoteInfoChanged: false,
  infoRevision: 0,
  hasChanges: false,
  protocolDirty: false,
  protocolSaving: false,
  loading: false,
  stateListWithCount: [],
  childEventList: [],
};

export const SecurityEventsReducer = createReducer(
  securityEventsInitialState,
  on(SecurityEventsActions.selectItem, (state, payload) => ({
    ...securityEventsInitialState,
    id: payload.id,
  })),
  on(
    SecurityEventsActions.getStateListWithCount,
    SecurityEventsActions.requestCreateItem,
    SecurityEventsActions.requestUpdateItem,
    SecurityEventsActions.requestSetToWorking,
    SecurityEventsActions.requestSetToCompleted,
    SecurityEventsActions.requestSetToClose,
    SecurityEventsActions.requestSetToCancel,
    SecurityEventsActions.requestSetToArchive,
    SecurityEventsActions.requestAddParentSecurityEvent,
    SecurityEventsActions.requestAddChildSecurityEvent,
    SecurityEventsActions.requestAddChildDisruption,
    SecurityEventsActions.requestDeleteChildEvent,
    SecurityEventsActions.requestTags,
    SecurityEventsActions.requestEditTags,
    SecurityEventsActions.requestDeleteSystemTag,
    SecurityEventsActions.requestChildEventList,
    (state) => ({
      ...state,
      loading: true,
    }),
  ),
  on(SecurityEventsActions.getStateListWithCountSuccess, (state, payload) => ({
    ...state,
    stateListWithCount: payload.list,
    loading: false,
  })),
  on(SecurityEventsActions.discardOwnerDraft, (state, { id }) =>
    state.id === id ? { ...state, form: null, hasChanges: false } : state,
  ),
  on(SecurityEventsActions.protocolEditState, (state, { id, dirty, saving }) =>
    state.id === id
      ? { ...state, protocolDirty: dirty, protocolSaving: saving }
      : state,
  ),
  on(SecurityEventsActions.requestItemInfoSuccess, (state, payload) => {
    if (state.id != null && payload.item?.id !== state.id) {
      return { ...state, loading: false };
    }

    if (isStaleInfoResponse(state.infoRevision, payload.observedRevision)) {
      return { ...state, loading: false };
    }

    return {
      ...state,
      id: payload.item.id,
      item: payload.item,
      pendingItem: null,
      remoteInfoChanged: false,
      hasChanges:
        payload.item.responseProcessMode === 'PROTOCOL' &&
        state.id === payload.item.id
          ? state.hasChanges
          : false,
      loading: false,
    };
  }),
  on(SecurityEventsActions.requestCreateItemSuccess, (state, payload) => ({
    ...state,
    id: payload.item.id,
    item: payload.item,
    pendingItem: null,
    remoteInfoChanged: false,
    hasChanges: false,
    loading: false,
  })),
  on(SecurityEventsActions.requestUpdateItemSuccess, (state, payload) => ({
    ...state,
    id: payload.item.id,
    item: payload.item,
    pendingItem: null,
    remoteInfoChanged: false,
    hasChanges: false,
    loading: false,
  })),
  on(SecurityEventsActions.requestTagsSuccess, (state, { tags }) => ({
    ...state,
    tags,
    loading: false,
  })),
  on(SecurityEventsActions.requestChildEventListSuccess, (state, payload) => ({
    ...state,
    childEventList: payload?.childEventList ?? [],
  })),
  on(
    SecurityEventsActions.requestSetToWorkingSuccess,
    SecurityEventsActions.requestSetToCompletedSuccess,
    SecurityEventsActions.requestSetToCloseSuccess,
    SecurityEventsActions.requestSetToCancelSuccess,
    SecurityEventsActions.requestSetToArchiveSuccess,
    SecurityEventsActions.requestAddParentSecurityEventSuccess,
    SecurityEventsActions.requestAddChildSecurityEventSuccess,
    SecurityEventsActions.requestAddChildDisruptionSuccess,
    SecurityEventsActions.requestDeleteChildEventSuccess,
    SecurityEventsActions.requestEditTagsSuccess,
    SecurityEventsActions.requestDeleteSystemTagSuccess,
    SecurityEventsActions.requestChildEventListSuccess,
    (state) => ({
      ...state,
      hasChanges:
        state.item?.responseProcessMode === 'PROTOCOL'
          ? state.hasChanges
          : false,
      loading: false,
    }),
  ),
  on(
    SecurityEventsActions.getStateListWithCountError,
    SecurityEventsActions.requestItemInfoError,
    SecurityEventsActions.requestCreateItemError,
    SecurityEventsActions.requestUpdateItemError,
    SecurityEventsActions.requestSetToWorkingError,
    SecurityEventsActions.requestSetToCompletedError,
    SecurityEventsActions.requestSetToCloseError,
    SecurityEventsActions.requestSetToCancelError,
    SecurityEventsActions.requestSetToArchiveError,
    SecurityEventsActions.requestAddParentSecurityEventError,
    SecurityEventsActions.requestAddChildSecurityEventError,
    SecurityEventsActions.requestAddChildDisruptionError,
    SecurityEventsActions.requestDeleteChildEventError,
    SecurityEventsActions.requestTagsError,
    SecurityEventsActions.requestEditTagsError,
    SecurityEventsActions.requestChildEventListError,
    (state) => ({
      ...state,
      loading: false,
    }),
  ),

  on(
    SecurityEventsActions.requestEditTagsError,
    SecurityEventsActions.requestDeleteSystemTagError,
    (state, payload) => ({
      ...state,
      tags: [...state.tags],
      error: payload.error,
      status: EStateStatus.ERROR,
    }),
  ),

  on(SecurityEventsActions.securityEventInfoReceived, (state, { item }) =>
    applyRemoteSecurityInfo(state, item),
  ),
  on(SecurityEventsActions.applyPendingSecurityEventInfo, (state) => {
    if (!state.pendingItem) {
      return state;
    }

    return {
      ...state,
      item: state.pendingItem,
      pendingItem: null,
      remoteInfoChanged: false,
      form: null,
      hasChanges: false,
    };
  }),
  on(SecurityEventsActions.setFormValues, (state, payload) => {
    const someField = Object.values(payload.form).some((v) => !!v);
    const hasChanges: boolean =
      someField && !compareObjects(payload.form, state.item, false);

    return {
      ...state,
      form: payload.form,
      hasChanges,
    };
  }),
);

function isStaleInfoResponse(
  infoRevision: number,
  observedRevision: number | undefined,
): boolean {
  return (
    typeof observedRevision === 'number' && observedRevision !== infoRevision
  );
}

function applyRemoteSecurityInfo(
  state: ISecurityEventsState,
  next: IInitEventSecurityInfo,
): ISecurityEventsState {
  const observed: ISecurityEventsState = {
    ...state,
    infoRevision: state.infoRevision + 1,
  };

  if (observed.item && compareObjects(next, observed.item, false)) {
    if (!observed.remoteInfoChanged && !observed.pendingItem) {
      return observed;
    }

    return {
      ...observed,
      pendingItem: null,
      remoteInfoChanged: false,
    };
  }

  if (observed.hasChanges) {
    return {
      ...observed,
      pendingItem: next,
      remoteInfoChanged: true,
    };
  }

  return {
    ...observed,
    item: next,
    pendingItem: null,
    remoteInfoChanged: false,
  };
}

export const SecurityEventsFeature = createFeature({
  name: 'SecurityEvents',
  reducer: SecurityEventsReducer,
});
